import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs, doc, getDoc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { Star, Filter, Check, X, AlertTriangle, Code2, ExternalLink, Clock } from 'lucide-react';
import ReviewDetailModal from '../../../components/reviews/ReviewDetailModal';
import ModerationModal from '../../../components/reviews/ModerationModal';
import MarketingModal from '../../../components/reviews/MarketingModal';
import { toast } from 'react-toastify';
import { AllowedDomain } from '../../../interfaces/AllowedDomain';

interface Review {
  id: string;
  productId: string;
  sellerId: string | null;
  userId: string;
  userName: string;
  userPhoto?: string;
  rating: number;
  comment: string;
  verified: boolean;
  orderId: string;
  status: 'pending' | 'active' | 'rejected';
  moderationStatus: 'approved' | 'rejected' | null;
  moderatedAt: string | null;
  moderatedBy: string | null;
  moderationFeedback?: string;
  createdAt: string;
  updatedAt: string;
  productName?: string;
  productImage?: string;
}

interface ReviewsPageState {
  reviews: Review[];
  loading: boolean;
  filters: {
    status: string;
  };
  showWidgetModal: boolean;
  allowedDomains: AllowedDomain[];
  newDomain: string;
  editingDomain: AllowedDomain | null;
  selectedReviews: string[];
  showModerationModal: boolean;
  moderationAction: 'approve' | 'reject' | null;
  selectedReview: Review | null;
  showDetailModal: boolean;
  widgetCode: string;
  widgetModalOpen: boolean;
  showMarketingModal: boolean;
}

const moderateMultipleReviews = async (reviewIds: string[], action: 'approve' | 'reject', moderatorId: string, feedback?: string) => {
  try {
    const batch = db.batch();
    for (const reviewId of reviewIds) {
      const reviewRef = doc(db, 'reviews', reviewId);
      batch.update(reviewRef, {
        status: action === 'approve' ? 'active' : 'rejected',
        moderationStatus: action === 'approve' ? 'approved' : 'rejected',
        moderatedAt: new Date().toISOString(),
        moderatedBy: moderatorId,
        moderationFeedback: feedback,
      });
    }
    await batch.commit();
    return true;
  } catch (error) {
    console.error('Erro ao moderar reviews em lote:', error);
    return false;
  }
};

const moderateReview = async (reviewId: string, action: 'approve' | 'reject', moderatorId: string, feedback?: string) => {
  try {
    const reviewRef = doc(db, 'reviews', reviewId);
    await updateDoc(reviewRef, {
      status: action === 'approve' ? 'active' : 'rejected',
      moderationStatus: action === 'approve' ? 'approved' : 'rejected',
      moderatedAt: new Date().toISOString(),
      moderatedBy: moderatorId,
      moderationFeedback: feedback,
    });
    return true;
  } catch (error) {
    console.error('Erro ao moderar review:', error);
    return false;
  }
};

const ReviewsPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [allReviews, setAllReviews] = useState<Review[]>([]);
  const [state, setState] = useState<ReviewsPageState>({
    reviews: [],
    loading: true,
    filters: {
      status: 'pending'
    },
    showWidgetModal: false,
    allowedDomains: [],
    newDomain: '',
    editingDomain: null,
    selectedReviews: [],
    showModerationModal: false,
    moderationAction: null,
    selectedReview: null,
    showDetailModal: false,
    widgetCode: '',
    widgetModalOpen: false,
    showMarketingModal: false
  });

  useEffect(() => {
    if (currentUser) {
      loadReviews();
    }
  }, [currentUser, state.filters]);

  const loadReviews = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const reviewsRef = collection(db, 'reviews');
      
      // Primeiro, buscar os produtos do vendedor
      const productsRef = collection(db, 'products');
      const productsQuery = query(
        productsRef,
        where('companyId', '==', currentUser?.companyId)
      );
      
      const productsSnap = await getDocs(productsQuery);
      const productIds = productsSnap.docs.map(doc => doc.id);

      // Buscar todas as reviews baseado no status
      let reviewsQuery;
      if (state.filters.status === 'pending') {
        reviewsQuery = query(
          reviewsRef,
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
      } else if (state.filters.status === 'active') {
        reviewsQuery = query(
          reviewsRef,
          where('status', '==', 'active'),
          where('moderationStatus', '==', 'approved'),
          orderBy('createdAt', 'desc')
        );
      } else {
        reviewsQuery = query(
          reviewsRef,
          where('status', '==', 'rejected'),
          where('moderationStatus', '==', 'rejected'),
          orderBy('createdAt', 'desc')
        );
      }

      // Buscar TODAS as reviews para estatísticas
      const allReviewsQuery = query(reviewsRef, orderBy('createdAt', 'desc'));
      const allReviewsSnap = await getDocs(allReviewsQuery);
      const allReviewsList = allReviewsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Review[];

      // Filtrar todas as reviews pelos produtos do vendedor
      const filteredAllReviews = allReviewsList.filter(review => {
        const reviewProductId = review.productId.split('-')[0];
        return productIds.includes(reviewProductId);
      });

      // Buscar reviews filtradas pelo status atual
      const reviewsSnap = await getDocs(reviewsQuery);
      const filteredReviews = reviewsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Review[];

      // Filtrar reviews do status atual pelos produtos do vendedor
      const filteredCurrentReviews = filteredReviews.filter(review => {
        const reviewProductId = review.productId.split('-')[0];
        return productIds.includes(reviewProductId);
      });

      // Adicionar informações do produto a cada review
      const reviewsWithProducts = await Promise.all(
        filteredCurrentReviews.map(async (review) => {
          const productId = review.productId.split('-')[0];
          const productDoc = productsSnap.docs.find(doc => doc.id === productId);
          
          if (productDoc) {
            const productData = productDoc.data();
            return {
              ...review,
              productName: productData.name,
              productImage: productData.mainImage
            };
          }
          return review;
        })
      );

      setAllReviews(filteredAllReviews);
      setState(prev => ({ ...prev, reviews: reviewsWithProducts }));
    } catch (error) {
      console.error('Erro ao carregar reviews:', error);
      toast.error('Erro ao carregar reviews. Tente novamente.');
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleStatusChange = (status: 'pending' | 'active' | 'rejected') => {
    setState(prev => ({ ...prev, filters: { ...prev.filters, status } }));
  };

  const getStatusColor = (status: string, moderationStatus: string | null) => {
    if (status === 'pending' && moderationStatus === null) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (status === 'active' && moderationStatus === 'approved') {
      return 'bg-green-100 text-green-800';
    }
    if (status === 'rejected' && moderationStatus === 'rejected') {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string, moderationStatus: string | null) => {
    if (status === 'pending' && moderationStatus === null) {
      return <AlertTriangle className="w-4 h-4" />;
    }
    if (status === 'active' && moderationStatus === 'approved') {
      return <Check className="w-4 h-4" />;
    }
    if (status === 'rejected' && moderationStatus === 'rejected') {
      return <X className="w-4 h-4" />;
    }
    return <AlertTriangle className="w-4 h-4" />;
  };

  const getStatusText = (status: string, moderationStatus: string | null) => {
    if (status === 'pending' && moderationStatus === null) {
      return 'Pendente';
    }
    if (status === 'active' && moderationStatus === 'approved') {
      return 'Aprovada';
    }
    if (status === 'rejected' && moderationStatus === 'rejected') {
      return 'Rejeitada';
    }
    return 'Pendente';
  };

  const handleModeration = async (action: 'approve' | 'reject', feedback?: string) => {
    try {
      setState(prev => ({ ...prev, showModerationModal: false }));
      setState(prev => ({ ...prev, loading: true }));

      if (state.selectedReviews.length > 0) {
        // Moderação em lote
        const success = await moderateMultipleReviews(
          state.selectedReviews,
          action,
          currentUser!.companyId,
          feedback
        );

        if (success) {
          toast.success(`${state.selectedReviews.length} reviews ${action === 'approve' ? 'aprovadas' : 'rejeitadas'} com sucesso`);
          setState(prev => ({ ...prev, selectedReviews: [] }));
        }
      } else if (state.selectedReview) {
        // Moderação individual
        const success = await moderateReview(
          state.selectedReview.id,
          action,
          currentUser!.companyId,
          feedback
        );

        if (success) {
          toast.success(`Review ${action === 'approve' ? 'aprovada' : 'rejeitada'} com sucesso`);
          setState(prev => ({ ...prev, selectedReview: null }));
        }
      }

      await loadReviews();
    } catch (error) {
      console.error('Erro na moderação:', error);
      toast.error('Erro ao processar ação. Tente novamente.');
    } finally {
      setState(prev => ({ ...prev, loading: false }));
      setState(prev => ({ ...prev, moderationAction: null }));
    }
  };

  // Função para carregar os domínios permitidos
  const loadAllowedDomains = async () => {
    try {
      const domainsRef = collection(db, 'allowed_domains');
      const domainsQuery = query(
        domainsRef,
        where('companyId', '==', currentUser?.companyId),
        where('status', '==', 'active')
      );
      const domainsSnap = await getDocs(domainsQuery);
      const domains = domainsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AllowedDomain[];
      setState(prev => ({ ...prev, allowedDomains: domains }));
    } catch (error) {
      console.error('Erro ao carregar domínios:', error);
      toast.error('Erro ao carregar domínios permitidos');
    }
  };

  // Função para adicionar novo domínio
  const addDomain = async () => {
    try {
      if (!state.newDomain.trim()) {
        toast.error('Por favor, insira um domínio válido');
        return;
      }

      const domain = state.newDomain.toLowerCase().trim();
      const domainsRef = collection(db, 'allowed_domains');
      await addDoc(domainsRef, {
        domain,
        companyId: currentUser?.companyId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active'
      });

      toast.success('Domínio adicionado com sucesso');
      setState(prev => ({ ...prev, newDomain: '' }));
      await loadAllowedDomains();
    } catch (error) {
      console.error('Erro ao adicionar domínio:', error);
      toast.error('Erro ao adicionar domínio');
    }
  };

  // Função para remover domínio
  const removeDomain = async (domainId: string) => {
    try {
      const domainRef = doc(db, 'allowed_domains', domainId);
      await updateDoc(domainRef, {
        status: 'inactive',
        updatedAt: new Date().toISOString()
      });

      toast.success('Domínio removido com sucesso');
      await loadAllowedDomains();
    } catch (error) {
      console.error('Erro ao remover domínio:', error);
      toast.error('Erro ao remover domínio');
    }
  };

  useEffect(() => {
    if (state.showWidgetModal) {
      loadAllowedDomains();
    }
  }, [state.showWidgetModal]);

  const renderWidgetModal = () => {
    if (!state.showWidgetModal) return null;

    const widgetCode = `<!-- Widget de Avaliações LogNow -->
<div id="reviews-container"></div>
<script src="https://lognow.pt/widgets/ReviewsWidget.js"></script>
<script>
    new ReviewsWidget('reviews-container', {
        theme: 'light',
        autoplay: true,
        interval: 5000,
        sellerId: '${currentUser?.companyId}' // ID do vendedor
    });
</script>`;

    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center ${state.showWidgetModal ? '' : 'hidden'}`}>
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Widget de Avaliações</h2>
            <button
              onClick={() => setState(prev => ({ ...prev, showWidgetModal: false }))}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600">
              Para exibir as suas avaliações no seu site ou blog, copie e cole o código abaixo no local onde deseja que o widget apareça:
            </p>
            
            <div className="relative">
              <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
                {widgetCode}
              </pre>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(widgetCode);
                  toast.success('Código copiado para a área de transferência!');
                }}
                className="absolute top-2 right-2 bg-blue-500 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-600 transition-colors"
              >
                Copiar Código
              </button>
            </div>
            
            <div className="mt-4 bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">Personalização</h3>
              <p className="text-blue-600 text-sm">
                Você pode personalizar o widget com as seguintes opções:
              </p>
              <ul className="list-disc list-inside text-sm text-blue-600 mt-2">
                <li>theme: 'light' ou 'dark' - Define o tema do widget</li>
                <li>autoplay: true/false - Ativa/desativa a rotação automática</li>
                <li>interval: número em ms - Define o intervalo entre as avaliações</li>
              </ul>
            </div>
            
            {/* Gestão de Domínios */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Domínios Permitidos</h3>
              <p className="text-sm text-gray-600 mb-4">
                Adicione os domínios onde o widget será utilizado para evitar problemas de CORS.
              </p>
              
              {/* Formulário para adicionar domínio */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={state.newDomain}
                  onChange={e => setState(prev => ({ ...prev, newDomain: e.target.value }))}
                  placeholder="exemplo.com"
                  className="flex-1 p-2 border rounded"
                />
                <button
                  onClick={addDomain}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Adicionar
                </button>
              </div>

              {/* Lista de domínios */}
              <div className="space-y-2">
                {state.allowedDomains.map(domain => (
                  <div key={domain.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span>{domain.domain}</span>
                    <button
                      onClick={() => removeDomain(domain.id!)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const calculateStats = (reviews: Review[]) => {
    const approved = reviews.filter(r => r.status === 'active' && r.moderationStatus === 'approved');
    const rejected = reviews.filter(r => r.status === 'rejected' && r.moderationStatus === 'rejected');
    const averageRating = approved.length > 0 
      ? (approved.reduce((sum, r) => sum + r.rating, 0) / approved.length).toFixed(1)
      : '0.0';

    return {
      averageRating,
      totalApproved: approved.length,
      totalRejected: rejected.length
    };
  };

  if (state.loading) {
    return <div className="flex justify-center items-center h-full">Carregando...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Reviews</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setState(prev => ({ ...prev, showMarketingModal: true }))}
            className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg px-4 py-2.5 font-medium hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            Ver Página de Reviews
          </button>
          <button
            onClick={() => handleStatusChange('pending')}
            className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg px-4 py-2.5 font-medium hover:bg-gray-50 transition-colors"
          >
            <Clock className="w-5 h-5" />
            Pendentes
            {state.reviews.filter(r => r.status === 'pending').length > 0 && (
              <span className="bg-secondary text-white text-xs px-2 py-0.5 rounded-full">
                {state.reviews.filter(r => r.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => handleStatusChange('active')}
            className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg px-4 py-2.5 font-medium hover:bg-gray-50 transition-colors"
          >
            <Check className="w-5 h-5" />
            Aprovadas
          </button>
          <button
            onClick={() => handleStatusChange('rejected')}
            className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg px-4 py-2.5 font-medium hover:bg-gray-50 transition-colors"
          >
            <X className="w-5 h-5" />
            Rejeitadas
          </button>
          <button
            onClick={() => setState(prev => ({ ...prev, showWidgetModal: true }))}
            className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg px-4 py-2.5 font-medium hover:bg-gray-50 transition-colors"
          >
            <Code2 className="w-5 h-5" />
            Widget
          </button>
        </div>
      </div>

      {/* Widget de Estatísticas */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Média de Avaliações</p>
              <div className="flex items-center mt-1">
                <span className="text-2xl font-bold">{calculateStats(allReviews).averageRating}</span>
                <div className="flex ml-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < parseInt(calculateStats(allReviews).averageRating)
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Reviews Aprovadas</p>
              <div className="flex items-center mt-1">
                <span className="text-2xl font-bold text-green-600">
                  {calculateStats(allReviews).totalApproved}
                </span>
                <Check className="w-5 h-5 ml-2 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Reviews Rejeitadas</p>
              <div className="flex items-center mt-1">
                <span className="text-2xl font-bold text-red-600">
                  {calculateStats(allReviews).totalRejected}
                </span>
                <X className="w-5 h-5 ml-2 text-red-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setState(prev => ({ ...prev, selectedReviews: prev.reviews.map(r => r.id) }));
                    } else {
                      setState(prev => ({ ...prev, selectedReviews: [] }));
                    }
                  }}
                  checked={state.selectedReviews?.length === state.reviews.length && state.reviews.length > 0}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Produto
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Avaliação
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {state.reviews.map((review) => (
              <tr key={review.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={state.selectedReviews.includes(review.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setState(prev => ({ ...prev, selectedReviews: [...state.selectedReviews, review.id] }));
                      } else {
                        setState(prev => ({ ...prev, selectedReviews: state.selectedReviews.filter(id => id !== review.id) }));
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {review.productImage && (
                      <img src={review.productImage} alt="" className="h-10 w-10 rounded-md object-cover mr-3" />
                    )}
                    <div className="text-sm font-medium text-gray-900">
                      {review.productName || 'Produto não encontrado'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {review.userPhoto ? (
                      <img src={review.userPhoto} alt="" className="h-8 w-8 rounded-full mr-2" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-200 mr-2" />
                    )}
                    <div className="text-sm font-medium text-gray-900">{review.userName}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-gray-600">{review.rating}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(review.status, review.moderationStatus)}`}>
                    {getStatusIcon(review.status, review.moderationStatus)}
                    <span className="ml-1">
                      {getStatusText(review.status, review.moderationStatus)}
                    </span>
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(review.createdAt).toLocaleDateString('pt-PT')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => {
                      setState(prev => ({ ...prev, selectedReview: review }));
                      setState(prev => ({ ...prev, showDetailModal: true }));
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Ver Detalhes
                  </button>
                  {review.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          setState(prev => ({ ...prev, moderationAction: 'approve' }));
                          setState(prev => ({ ...prev, showModerationModal: true }));
                          setState(prev => ({ ...prev, selectedReview: review }));
                        }}
                        className="text-green-600 hover:text-green-900 mr-4"
                      >
                        Aprovar
                      </button>
                      <button
                        onClick={() => {
                          setState(prev => ({ ...prev, moderationAction: 'reject' }));
                          setState(prev => ({ ...prev, showModerationModal: true }));
                          setState(prev => ({ ...prev, selectedReview: review }));
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Rejeitar
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {state.showModerationModal && state.moderationAction && (
        <ModerationModal
          isOpen={state.showModerationModal}
          onClose={() => {
            setState(prev => ({ ...prev, showModerationModal: false }));
            setState(prev => ({ ...prev, moderationAction: null }));
          }}
          onConfirm={(feedback) => handleModeration(state.moderationAction, feedback)}
          action={state.moderationAction}
          isBatch={state.selectedReviews.length > 0}
          selectedCount={state.selectedReviews.length}
        />
      )}
      {state.showDetailModal && state.selectedReview && (
        <ReviewDetailModal
          isOpen={state.showDetailModal}
          onClose={() => {
            setState(prev => ({ ...prev, showDetailModal: false }));
            setState(prev => ({ ...prev, selectedReview: null }));
          }}
          review={state.selectedReview}
        />
      )}
      {renderWidgetModal()}
      {state.showMarketingModal && (
        <MarketingModal
          isOpen={state.showMarketingModal}
          onClose={() => setState(prev => ({ ...prev, showMarketingModal: false }))}
          onViewPage={() => {
            window.open(`/reviews/${currentUser?.companyId}`, '_blank');
            setState(prev => ({ ...prev, showMarketingModal: false }));
          }}
        />
      )}
    </div>
  );
};

export default ReviewsPage;
